import React, { useRef, useState, useEffect, useCallback } from "react";
import * as faceapi from "@vladmandic/face-api";
import axios from "axios";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "react-toastify";
import { apiKey } from "../../config/config";
import { languageHelper } from "../../config/helperFunctions";

export default function FaceLogin() {
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const [status, setStatus] = useState("Loading face detection models...");
  const [modelsLoaded, setModelsLoaded] = useState(false);
  const [faceFound, setFaceFound] = useState(false);
  const [verifying, setVerifying] = useState(false);
  // const [logo, setLogo] = useState(null);

  // ✅ Load models for @vladmandic/face-api
  useEffect(() => {
    const loadModels = async () => {
      try {
        const MODEL_URL = `${process.env.PUBLIC_URL}/models`;
        await faceapi.tf.setBackend("webgl");
        await faceapi.tf.ready();
        await faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL);
        await faceapi.nets.faceLandmark68Net.loadFromUri(MODEL_URL);
        await faceapi.nets.faceRecognitionNet.loadFromUri(MODEL_URL);
        setModelsLoaded(true);
        setStatus("Models loaded. Starting camera...");
      } catch (err) {
        console.error("Model loading error:", err);
        setStatus("Failed to load models.");
      }
    };
    loadModels();
  }, []);

  // ✅ Start camera
  const startVideo = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setStatus("Camera started. Please look into the camera...");
    } catch (err) {
      console.error("Camera start error:", err);
      setStatus("Camera permission denied or unavailable.");
    }
  }, []);

  // ✅ Stop camera safely
  const stopCamera = useCallback(() => {
    const video = videoRef.current;
    if (video && video.srcObject) {
      const stream = video.srcObject;
      stream.getTracks().forEach((track) => track.stop());
      video.pause();
      video.srcObject = null;
      console.log("Camera stopped completely");
    }
  }, []);

  // ✅ Face detection logic
  const detectFace = useCallback(async () => {
    if (!modelsLoaded || verifying || !videoRef.current) return;

    try {
      const detection = await faceapi
        .detectSingleFace(
          videoRef.current,
          new faceapi.TinyFaceDetectorOptions({ inputSize: 320, scoreThreshold: 0.5 })
        )
        .withFaceLandmarks()
        .withFaceDescriptor();

      if (detection) {
        setFaceFound(true);
        setVerifying(true);
        setStatus("Face detected. Verifying...");
        await verifyFace(detection.descriptor);
      } else {
        setFaceFound(false);
      }
    } catch (err) {
      console.error("Detection error:", err);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelsLoaded, verifying]);

  // ✅ API: Verify face
  const verifyFace = async (faceDescriptor) => {
    try {
      const res = await axios.post(`${apiKey}/api/verify-face-login`, {
        face_descriptor: Array.from(faceDescriptor),
      });

      if (res.data.success) {
        stopCamera();
        localStorage.setItem("rms-admin", JSON.stringify(res.data.data));

        toast.success(res.data.message || "Face verified successfully!", {
          position: "bottom-right",
          autoClose: 4000,
          theme: "colored",
        });

        setStatus("Face verified! Redirecting...");
        setTimeout(() => navigate("/POS/main"), 1500);
      } else if (
        res.data.message === "Unauthorised." ||
        res.data?.data?.error === "Unauthorised"
      ) {
        stopCamera();
        setStatus("Session expired. Redirecting to login...");
        setTimeout(() => navigate("/admin/login"), 1500);
      } else {
        setStatus("Face not recognized. Try again...");
        setTimeout(() => {
          setVerifying(false);
          setFaceFound(false);
        }, 2000);
      }
    } catch (err) {
      console.error("Verification error:", err);
      setStatus("Server error during verification.");
      stopCamera();
      setTimeout(() => navigate("/admin/login"), 1500);
    } finally {
      setVerifying(false);
    }
  };

  // ✅ Start camera when models are ready
  useEffect(() => {
    if (modelsLoaded) startVideo();
    return () => stopCamera();
  }, [modelsLoaded, startVideo, stopCamera]);

  // ✅ Repeated detection loop
  useEffect(() => {
    if (!modelsLoaded) return;
    const interval = setInterval(detectFace, 1000);
    return () => clearInterval(interval);
  }, [modelsLoaded, detectFace]);

  // ✅ Status color handler
  const getStatusColor = () => {
    const txt = status.toLowerCase();
    if (txt.includes("error") || txt.includes("fail") || txt.includes("not recognized"))
      return "text-red-600";
    if (txt.includes("verifying") || txt.includes("allow") || txt.includes("loading"))
      return "text-yellow-600";
    if (txt.includes("verified") || txt.includes("redirecting"))
      return "text-green-600";
    return "text-gray-700";
  };

  return (
    <main
      className="relative min-h-screen w-full grid place-items-center px-4"
      style={{
        background:
          "linear-gradient(rgba(255,255,255,0.55), rgba(255,255,255,0.55)), url('/assets/login/login_bg.jpg') center/cover no-repeat",
      }}
    >
      {/* Background dots */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute left-[6%] top-[12%] w-3 h-3 bg-white/70 rounded-full" />
        <div className="absolute left-[16%] top-[34%] w-6 h-6 bg-white/70 rounded-full" />
        <div className="absolute right-[10%] top-[14%] w-8 h-8 bg-white/60 rounded-full" />
        <div className="absolute right-[18%] top-[40%] w-2.5 h-2.5 bg-white/70 rounded-full" />
        <div className="absolute left-[8%] bottom-[18%] w-10 h-10 bg-white/65 rounded-full" />
        <div className="absolute right-[6%] bottom-[22%] w-4 h-4 bg-white/70 rounded-full" />
      </div>

      {/* left illustration */}
      <div className="hidden md:flex absolute left-6 xl:left-16 bottom-10 items-center justify-center z-10">
        <div
          className="relative w-[260px] h-[260px] md:w-[300px] md:h-[300px] rounded-full bg-white/70 backdrop-blur-sm shadow-sm overflow-visible"
        >
          <img
            src="/assets/login/login1.png"
            alt="Left illustration"
            className="absolute w-[120%] max-w-none -bottom-8 right-0 object-contain drop-shadow"
          />
        </div>
      </div>

      {/* right illustration */}
      <div className="hidden md:flex absolute right-6 xl:right-16 top-20 items-center justify-center z-10">
        <div className="relative w-[240px] h-[240px] md:w-[280px] md:h-[280px] rounded-full bg-white/70 backdrop-blur-sm shadow-sm grid place-items-center">
          <img
            src="/assets/login/login2.png"
            alt="Right illustration"
            className="absolute w-[130%] max-w-none right-0 object-contain drop-shadow"
          />
        </div>
      </div>

      {/* Center card */}
      <div className="relative bg-white/95 rounded-2xl shadow-lg w-full max-w-[560px]">
        <div className="px-10 pt-10 pb-6 flex flex-col items-center">
          {/* <img
            src={logo || "/assets/logo/imdady_logo.png"}
            alt="Logo"
            className="h-10 mb-6"
          /> */}
          <h1 className="text-center text-[#222] font-semibold text-2xl md:text-[26px] mb-1">
            {languageHelper("Face Recognition Login")}
          </h1>
          <p className="text-center text-[#8C8C8C] text-sm mb-6">
            Please align your face with the camera to verify
          </p>

          {/* Camera box */}
          <div className="relative w-[480px] h-[360px] rounded-xl border border-gray-300 overflow-hidden bg-black shadow-inner">
            <video
              ref={videoRef}
              autoPlay
              muted
              width="480"
              height="360"
              className="object-cover w-full h-full"
            ></video>
            <div
              className={`absolute inset-0 rounded-xl transition-all duration-500 border-4 ${
                faceFound
                  ? "border-green-500 shadow-[0_0_20px_rgba(34,197,94,0.7)]"
                  : "border-transparent"
              }`}
            ></div>
          </div>

          <div className="mt-4">
            <span className={`text-base font-medium ${getStatusColor()}`}>
              {status}
            </span>
          </div>

          <Link
            to="/admin/login"
            onClick={stopCamera}
            className="mt-5 block w-full text-center rounded-md bg-gray-400 text-white py-2.5 font-medium hover:bg-gray-500 transition-all"
          >
            {languageHelper("Back to Login")}
          </Link>
        </div>
      </div>
    </main>
  );
}
